import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Box, Button, Stack, TextField } from '@mui/material';
import { FormProvider, useForm, type Control, type FieldValues, type SubmitHandler } from 'react-hook-form';

import { ProfesionalSelect } from '@/features/profesionales/components/ProfesionalSelect';
import { isApiError } from '@/lib/apiError';

import {
  establecimientoFormSchema,
  establecimientoUpdateFormSchema,
  type EstablecimientoFormValues,
  type EstablecimientoUpdateFormValues,
} from '../schemas';
import type { Establecimiento } from '../types';

import { FacturacionSection } from './FacturacionSection';
import { TelefonosContactoSection } from './TelefonosContactoSection';

interface EstablecimientoCreateFormProps {
  mode: 'create';
  defaultValues?: { uid_profesional_actual?: string };
  onSubmit: SubmitHandler<EstablecimientoFormValues>;
  onCancel: () => void;
  submitLabel: string;
  submitting: boolean;
  error?: unknown;
}

interface EstablecimientoEditFormProps {
  mode: 'edit';
  defaultValues: Establecimiento;
  onSubmit: SubmitHandler<EstablecimientoUpdateFormValues>;
  onCancel: () => void;
  submitLabel: string;
  submitting: boolean;
  error?: unknown;
}

type EstablecimientoFormProps =
  | EstablecimientoCreateFormProps
  | EstablecimientoEditFormProps;

const EMPTY_FACTURACION = {
  cuit: '',
  razon_social: '',
  email_facturacion: '',
  telefono_whatsapp_facturacion: '',
  envio_factura: '' as never,
};

export function EstablecimientoForm(props: EstablecimientoFormProps): JSX.Element {
  if (props.mode === 'create') {
    return <CreateForm {...props} />;
  }
  return <EditForm {...props} />;
}

function CreateForm({
  defaultValues,
  onSubmit,
  onCancel,
  submitLabel,
  submitting,
  error,
}: EstablecimientoCreateFormProps): JSX.Element {
  const formMethods = useForm<EstablecimientoFormValues>({
    resolver: zodResolver(establecimientoFormSchema),
    defaultValues: {
      nombre: '',
      firma_ganaderos: '',
      localidad: '',
      uid_profesional_actual: defaultValues?.uid_profesional_actual ?? '',
      facturacion: EMPTY_FACTURACION,
      telefonos_contacto: [],
    },
  });
  const { register, control, handleSubmit, formState: { errors, isDirty } } = formMethods;

  return (
    <FormProvider {...formMethods}>
      <FormBody
        register={register as never}
        errors={errors as never}
        control={control as never}
        onSubmit={handleSubmit(onSubmit)}
        onCancel={onCancel}
        submitLabel={submitLabel}
        submitting={submitting}
        isDirty={isDirty}
        error={error}
        mode="create"
      />
    </FormProvider>
  );
}

function EditForm({
  defaultValues,
  onSubmit,
  onCancel,
  submitLabel,
  submitting,
  error,
}: EstablecimientoEditFormProps): JSX.Element {
  const formMethods = useForm<EstablecimientoUpdateFormValues>({
    resolver: zodResolver(establecimientoUpdateFormSchema),
    defaultValues: {
      nombre: defaultValues.nombre,
      firma_ganaderos: defaultValues.firma_ganaderos ?? '',
      localidad: defaultValues.localidad ?? '',
      facturacion: {
        cuit: defaultValues.facturacion?.cuit ?? '',
        razon_social: defaultValues.facturacion?.razon_social ?? '',
        email_facturacion: defaultValues.facturacion?.email_facturacion ?? '',
        telefono_whatsapp_facturacion:
          defaultValues.facturacion?.telefono_whatsapp_facturacion ?? '',
        envio_factura: (defaultValues.facturacion?.envio_factura ?? '') as never,
      },
      telefonos_contacto: (defaultValues.telefonos_contacto ?? []).map((t) => ({
        numero: t.numero,
        nombre: t.nombre ?? '',
      })),
    },
  });
  const { register, control, handleSubmit, formState: { errors, isDirty } } = formMethods;

  return (
    <FormProvider {...formMethods}>
      <FormBody
        register={register as never}
        errors={errors as never}
        control={control as never}
        onSubmit={handleSubmit(onSubmit)}
        onCancel={onCancel}
        submitLabel={submitLabel}
        submitting={submitting}
        isDirty={isDirty}
        error={error}
        mode="edit"
      />
    </FormProvider>
  );
}

// Nested error shapes from RHF
type NestedErrors = {
  nombre?: { message?: string };
  firma_ganaderos?: { message?: string };
  localidad?: { message?: string };
  uid_profesional_actual?: { message?: string };
  facturacion?: {
    cuit?: { message?: string };
    razon_social?: { message?: string };
    email_facturacion?: { message?: string };
    telefono_whatsapp_facturacion?: { message?: string };
    envio_factura?: { message?: string };
  };
};

interface FormBodyProps {
  register: never;
  errors: never;
  control: Control<FieldValues>;
  onSubmit: () => void;
  onCancel: () => void;
  submitLabel: string;
  submitting: boolean;
  isDirty: boolean;
  error?: unknown;
  mode: 'create' | 'edit';
}

function FormBody({
  register,
  errors,
  control,
  onSubmit,
  onCancel,
  submitLabel,
  submitting,
  isDirty,
  error,
  mode,
}: FormBodyProps): JSX.Element {
  const errorMessage =
    isApiError(error) && error.isValidationError()
      ? error
          .validationErrors()
          .map((e) => `${e.field}: ${e.message}`)
          .join('. ')
      : isApiError(error)
        ? error.message
        : error instanceof Error
          ? error.message
          : null;

  const reg = register as unknown as (name: string) => Record<string, unknown>;
  const err = errors as unknown as NestedErrors;

  const flatErrors: Record<string, { message?: string } | undefined> = {
    'facturacion.cuit': err.facturacion?.cuit,
    'facturacion.razon_social': err.facturacion?.razon_social,
    'facturacion.email_facturacion': err.facturacion?.email_facturacion,
    'facturacion.telefono_whatsapp_facturacion':
      err.facturacion?.telefono_whatsapp_facturacion,
    'facturacion.envio_factura': err.facturacion?.envio_factura,
  };

  return (
    <form onSubmit={onSubmit} noValidate>
      <Stack spacing={3}>
        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

        <TextField
          label="Nombre del establecimiento"
          fullWidth
          required
          autoFocus
          {...reg('nombre')}
          error={!!err.nombre}
          helperText={err.nombre?.message ?? ' '}
        />

        <TextField
          label="Localidad"
          fullWidth
          {...reg('localidad')}
          error={!!err.localidad}
          helperText={err.localidad?.message ?? ' '}
        />

        <TextField
          label="Firma del ganadero"
          fullWidth
          multiline
          rows={2}
          {...reg('firma_ganaderos')}
          error={!!err.firma_ganaderos}
          helperText={err.firma_ganaderos?.message ?? ' '}
        />

        {mode === 'create' && (
          <ProfesionalSelect
            label="Profesional asignado"
            required
            {...reg('uid_profesional_actual')}
            error={!!err.uid_profesional_actual}
            helperText={err.uid_profesional_actual?.message ?? ' '}
          />
        )}

        <TelefonosContactoSection disabled={submitting} />

        <FacturacionSection register={reg} errors={flatErrors} control={control} />

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
          <Button onClick={onCancel} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={submitting || !isDirty}
          >
            {submitting ? 'Guardando...' : submitLabel}
          </Button>
        </Box>
      </Stack>
    </form>
  );
}
